import { addNewRelicPageAction } from '@ddc/new-relic';
import eventShouldBeTracked from './eventShouldBeTracked';

export const CDL_SUBSCRIBE_POLL_MS = 100;
export const CDL_SUBSCRIBE_TIMEOUT_MS = 30000;
export const CDL_SUBSCRIBE_SLOW_POLL_MS = 1000;
const NR_SAMPLE_PERCENT = 10;

const hasSubscribe = (win) =>
	typeof win?.coxAutoDataLayer?.subscribe === 'function';

const getCdlType = (cdl) => {
	if (Array.isArray(cdl)) {
		return 'array';
	}
	return cdl === null ? 'null' : typeof cdl;
};

/**
 * What the page's consent platform says, so a CDL library held back until the visitor consents can be told apart from one that is failing.
 * Each part is read separately and skipped if it throws (document.cookie can throw when the visitor blocks site data).
 *
 * @param {Window} win The window object
 * @returns {Object} consentPlatform (e.g. 'complyauto', from the Pixall consent handler script), cdlPrivacy (the raw cookie)
 * and one consent<Category> attribute per ComplyAuto consent category ('true' | 'false' | 'null')
 */
const getConsentState = (win) => {
	const state = {};
	try {
		const src = win.document.querySelector(
			'script[src*="/pixall-consent-management/"]'
		)?.src;
		const platform = src?.match(
			/\/pixall-consent-management\/([^/]+)\//
		)?.[1];
		if (platform) {
			state.consentPlatform = platform;
		}
	} catch (e) {
		// no consent platform script to read
	}
	try {
		const cdlPrivacy = win.document.cookie.match(
			/(?:^|;\s*)cdlPrivacy=([^;]*)/
		)?.[1];
		if (cdlPrivacy) {
			state.cdlPrivacy = decodeURIComponent(cdlPrivacy);
		}
	} catch (e) {
		// cookies unavailable
	}
	try {
		Object.entries(win.getConsentCa?.() || {}).forEach(
			([category, value]) => {
				state[
					`consent${category[0].toUpperCase()}${category.slice(1)}`
				] = String(value);
			}
		);
	} catch (e) {
		// no ComplyAuto consent API
	}
	return state;
};

/**
 * Subscribes `callback` to window.coxAutoDataLayer once its `subscribe` method exists, without holding up the rest of ws-tagging.
 *
 * The cdl project creates window.coxAutoDataLayer as a plain array when the CDL library hasn't loaded yet,
 * so `subscribe` can be missing when ws-tagging sets up its listeners.
 * CDL replays pushes it has already processed to new subscribers by default, so subscribing late doesn't lose events.
 * See https://ghe.coxautoinc.com/CA-DataFoundation/pixall-portal-remote-app-cdl-documentation/blob/main/docs/subscribe-to-events.md#receive-past-events
 *
 * Checks every pollMs, then every slowPollMs after timeoutMs, and never gives up: a consent platform can hold the CDL library
 * until the visitor consents, which can be well after the page loads.
 *
 * Reports a sampled wsTaggingCdlSubscribe PageAction when it had to wait (status: 'waited') or is still waiting at timeoutMs
 * (status: 'timeout', with the consent state). If subscribe shows up after that, it sends a follow-up 'waited' with
 * afterTimeout: true. Sampling is decided once so a timeout and its follow-up are reported together.
 *
 * @param {Window} win The window object
 * @param {Function} callback The CDL subscriber, called with (state, data, additionalInfo)
 * @param {Object} [options]
 * @param {number} [options.pollMs] How often to check for subscribe at first
 * @param {number} [options.timeoutMs] When to report the timeout and slow down
 * @param {number} [options.slowPollMs] How often to check for subscribe after timeoutMs
 * @returns {Function} Stops waiting without reporting
 */
const whenCdlSubscribeReady = (
	win,
	callback,
	{
		pollMs = CDL_SUBSCRIBE_POLL_MS,
		timeoutMs = CDL_SUBSCRIBE_TIMEOUT_MS,
		slowPollMs = CDL_SUBSCRIBE_SLOW_POLL_MS
	} = {}
) => {
	if (hasSubscribe(win)) {
		win.coxAutoDataLayer.subscribe(callback);
		return () => {};
	}

	const initialCdlType = getCdlType(win?.coxAutoDataLayer);
	const start = Date.now();
	const tracked = eventShouldBeTracked(NR_SAMPLE_PERCENT);
	let timedOut = false;
	let interval;

	const report = (status, props) => {
		if (tracked) {
			addNewRelicPageAction('wsTaggingCdlSubscribe', {
				status,
				...props,
				nrSamplePercent: NR_SAMPLE_PERCENT
			});
		}
	};

	// read win.coxAutoDataLayer fresh each time: the CDL library may replace the array stub or add subscribe to it
	const check = () => {
		const waitMs = Date.now() - start;
		if (hasSubscribe(win)) {
			clearInterval(interval);
			win.coxAutoDataLayer.subscribe(callback);
			report('waited', {
				...(timedOut && { afterTimeout: true }),
				waitMs,
				initialCdlType,
				...(timedOut && getConsentState(win))
			});
		} else if (!timedOut && waitMs >= timeoutMs) {
			timedOut = true;
			clearInterval(interval);
			interval = setInterval(check, slowPollMs);
			report('timeout', {
				waitMs,
				initialCdlType,
				...getConsentState(win)
			});
		}
	};

	interval = setInterval(check, pollMs);

	return () => clearInterval(interval);
};

export default whenCdlSubscribeReady;
